import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Role, getRoleCode } from "constant/Role"
import { Link } from "react-router-dom"
import { Wallet, Plus } from "lucide-react"
import BudgetPairModal from "./components/budget/BudgetPairModal"
import AddBudgetLineModal from "./components/budget/AddBudgetLineModal"
import BudgetByItemTable from "./components/budget/BudgetByItemTable"
import useProcurementFilters from "./hooks/useProcurementFilters"
import {
  useGetRequesterBudgetQuery,
  useGetActiveBudgetCategoriesQuery,
  useCreateBudgetCategoryMutation,
  useGetBudgetCentersQuery,
  useGetBudgetLinesQuery,
  useCreateBudgetLineMutation,
} from "shared/redux/endpoints/procurementV2"
import { formatVND, FISCAL_MONTH_OPTIONS, regionFromCenterCode } from "./constants"
import { Page, Button } from "./ui"
import {
  FilterBar,
  DataTable,
  KpiTile,
  KpiStrip,
  MoneyText,
  StatusPill,
  EmptyState,
  ErrorState,
  UtilizationBar,
  WaterfallBar,
  SegmentedControl,
  en,
  remainingAlert,
  budgetStatusOf,
} from "procurement/design"
import { buildBudgetTree, flattenTree, sortTree, usedPct, totalConsumed } from "procurement/design/format/budgetTree"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import { showToastSuccess, showToastError } from "utilities/toastUtil"

// Mirrors the BE role gate on budget-line POST / PUT / DELETE / allocation-policy PATCH.
const BUDGET_EDIT_ROLES = [Role.PROCUREMENT_MANAGER, Role.FINANCE, "ROLE_FINANCE_LEAD", Role.ADMIN]

const currentYear = new Date().getFullYear()
const currentYearStr = String(currentYear)
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) => {
  const y = currentYear + 1 - i
  return { value: String(y), label: String(y) }
})

const enrichRows = (rows, centers = []) => {
  const byId = Object.fromEntries(centers.map((c) => [String(c.id), c]))
  return (rows || []).map((r) => {
    const center = byId[String(r.centerId)] || {}
    const centerCode = r.centerCode || center.centerCode || ""
    return {
      ...r,
      centerCode,
      centerName: r.centerName || center.name || r.centerName,
      region: regionFromCenterCode(centerCode),
      remaining:
        r.remaining != null
          ? r.remaining
          : (Number(r.total) || 0) -
            (Number(r.committed) || 0) -
            (Number(r.approvedUnpaid) || 0) -
            (Number(r.approvedPaid) || 0),
    }
  })
}

// Child (depth > 0) tree rows map 1:1 to a GET /budgets row, so they carry the
// BE's own flag. Parent nodes are client-side sums the BE never saw — derive
// theirs from the summed remaining.
const overBudgetOf = (r) => {
  if (r.depth > 0 && r.source) {
    return { over: !!r.source.isOverBudget, amount: Number(r.source.overSpentAmount) || 0 }
  }
  const remaining = Number(r.remaining) || 0
  return { over: remaining < 0, amount: remaining < 0 ? -remaining : 0 }
}

const budgetColumns = (opts = {}) => {
  const { showStatus = false } = opts
  const cols = [
    { key: "name", header: en.budgetControl.dimension, strong: true, render: (r) => r.name },
    {
      key: "total",
      header: en.budget.total,
      align: "right",
      sortable: true,
      render: (r) => <MoneyText value={r.total} />,
    },
    {
      key: "committed",
      header: en.budget.committed,
      align: "right",
      sortable: true,
      render: (r) => <MoneyText value={r.committed} />,
    },
    {
      key: "approvedUnpaid",
      header: en.budget.approvedUnpaid,
      align: "right",
      sortable: true,
      render: (r) => <MoneyText value={r.approvedUnpaid} />,
    },
    {
      key: "approvedPaid",
      header: en.budget.approvedPaid,
      align: "right",
      sortable: true,
      render: (r) => <MoneyText value={r.approvedPaid} />,
    },
  ]
  if (showStatus) {
    cols.push({
      key: "consumed",
      header: en.budgetControl.totalConsumed,
      align: "right",
      sortable: true,
      tooltip: en.budgetControl.consumedTooltip,
      value: (r) => totalConsumed(r),
      render: (r) => <MoneyText value={totalConsumed(r)} />,
    })
  }
  cols.push(
    {
      key: "overPaidAmount",
      header: en.budget.overPaid,
      align: "right",
      sortable: true,
      render: (r) =>
        r.overPaidAmount > 0 ? (
          <span style={{ color: "var(--pc-danger)" }}>
            <MoneyText value={r.overPaidAmount} />
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "remaining",
      header: en.budget.remaining,
      align: "right",
      sortable: true,
      render: (r) => {
        const { over, amount } = overBudgetOf(r)
        return (
          <div>
            <MoneyText value={r.remaining} />
            {over && (
              <div style={{ color: "var(--pc-danger)", fontSize: 12 }}>{en.budget.overBy(formatVND(amount))}</div>
            )}
          </div>
        )
      },
    },
    {
      key: "util",
      header: en.budget.used,
      sortable: true,
      value: (r) => usedPct(r),
      render: (r) => {
        const pct = usedPct(r)
        const st = budgetStatusOf(pct)
        return (
          <div style={{ minWidth: 120 }}>
            <UtilizationBar
              committed={r.committed}
              unpaid={r.approvedUnpaid}
              paid={r.approvedPaid}
              remaining={r.remaining}
              total={r.total}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12 }}>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{Math.round(pct)}%</span>
              {showStatus && pct > 100 && <StatusPill status="over_budget" />}
            </div>
          </div>
        )
      },
    }
  )
  if (showStatus) {
    cols.push({
      key: "health",
      header: en.budgetControl.status,
      value: (r) => usedPct(r),
      render: (r) => {
        const st = budgetStatusOf(usedPct(r))
        return <StatusPill tone={st.tone} label={st.label} />
      },
    })
  }
  return cols
}

const RequesterBudgetView = () => {
  const { filters, setFilters, resetFilters } = useProcurementFilters({
    fiscalYear: currentYearStr,
    fiscalMonth: "",
    centerId: "",
    search: "",
    groupBy: "center",
  })

  const { data: centers } = useGetBudgetCentersQuery({ isActive: true })
  const centerOptions = useMemo(
    () => (centers || []).map((c) => ({ value: String(c.id), label: c.name })),
    [centers]
  )

  const { data, isFetching, isError: isFetchError, error, refetch } = useGetRequesterBudgetQuery({
    fiscalYear: filters.fiscalYear,
    fiscalMonth: filters.fiscalMonth || undefined,
    centerId: filters.centerId || undefined,
  })
  const notFound = Boolean(data?.notFound) || Boolean(isFetchError && error?.notFound)
  const isError = isFetchError && !error?.notFound
  const budgetRows = useMemo(
    () => enrichRows(notFound ? [] : data?.rows || [], centers),
    [data, notFound, centers]
  )

  const visible = useMemo(() => {
    const q = filters.search.trim().toLowerCase()
    return budgetRows.filter((r) => !q || (r.categoryName || "").toLowerCase().includes(q))
  }, [budgetRows, filters.search])

  const totals = useMemo(() => {
    const s = notFound ? null : data?.summary
    if (s && s.total != null) {
      return {
        total: s.total || 0,
        committed: s.committed || 0,
        approvedUnpaid: s.approvedUnpaid || 0,
        approvedPaid: s.approvedPaid || 0,
        remaining: s.remaining || 0,
        overPaidAmount: s.overPaidAmount || 0,
        isOverBudget: !!s.isOverBudget,
        overSpentAmount: s.overSpentAmount || 0,
      }
    }
    return visible.reduce(
      (acc, r) => {
        acc.total += r.total || 0
        acc.committed += r.committed || 0
        acc.approvedUnpaid += r.approvedUnpaid || 0
        acc.approvedPaid += r.approvedPaid || 0
        acc.remaining += r.remaining || 0
        acc.overPaidAmount += r.overPaidAmount || 0
        return acc
      },
      { total: 0, committed: 0, approvedUnpaid: 0, approvedPaid: 0, remaining: 0, overPaidAmount: 0, isOverBudget: false, overSpentAmount: 0 }
    )
  }, [data, visible, notFound])

  const hasDepartment = visible.some((r) => r.departmentId || r.department)
  // TODO(BE): GET /budgets items[] has no department or item-group breakdown.
  const groupBy = hasDepartment ? filters.groupBy : filters.groupBy === "department" ? "center" : filters.groupBy
  const tree = useMemo(() => buildBudgetTree(visible, groupBy), [visible, groupBy])
  const [expanded, setExpanded] = useState(() => new Set())
  const flat = useMemo(() => flattenTree(tree, expanded), [tree, expanded])

  const used = totals.total > 0 ? ((totals.total - totals.remaining) / totals.total) * 100 : 0
  const remainAlert = remainingAlert(totals.remaining, totals.total)
  const approved = (totals.approvedUnpaid || 0) + (totals.approvedPaid || 0)

  return (
    <Page breadcrumb={["Procurement", "Budget"]} title={en.budget.title} subtitle={en.budget.subtitle}>
      {isError ? (
        <ErrorState
          text={getApiErrorMessage(error, en.empty.loadErrorText)}
          action={
            <Button variant="secondary" size="sm" onClick={refetch}>
              {en.common.retry}
            </Button>
          }
        />
      ) : (
        <>
          <KpiStrip>
            <KpiTile label={en.budget.total} value={<MoneyText value={totals.total} compact align="left" />} hint={en.common.inCurrentList} />
            <KpiTile label={en.budget.committed} value={<MoneyText value={totals.committed} compact align="left" />} hint={en.common.inCurrentList} />
            <KpiTile
              label={en.budget.approved}
              value={<MoneyText value={approved} compact align="left" />}
              hint="Unpaid + paid, in the current list"
            />
            <KpiTile
              label={en.budget.remaining}
              value={<MoneyText value={totals.remaining} compact align="left" />}
              hint={`${Math.round(used)}% used`}
              emphasis
              danger={remainAlert.key === "critical"}
            />
            {totals.isOverBudget && (
              <KpiTile
                label={en.budget.overSpent}
                value={<MoneyText value={totals.overSpentAmount} compact align="left" />}
                hint={en.budget.overSpentHint}
                danger
              />
            )}
            {totals.overPaidAmount > 0 && (
              <KpiTile
                label={en.budget.overPaid}
                value={<MoneyText value={totals.overPaidAmount} compact align="left" />}
                hint="Paid beyond the remaining budget"
                danger
              />
            )}
          </KpiStrip>

          <FilterBar
            values={filters}
            onChange={setFilters}
            onReset={resetFilters}
            fields={[
              { key: "fiscalYear", label: en.filter.fiscalYear, type: "select", options: YEAR_OPTIONS, allowEmpty: false },
              { key: "fiscalMonth", label: en.filter.month, type: "select", options: FISCAL_MONTH_OPTIONS, allowEmpty: false },
              { key: "centerId", label: en.filter.center, type: "select", options: centerOptions, placeholder: en.filter.allCenters },
              { key: "search", label: en.filter.searchCategory, placeholder: en.filter.placeholder },
            ]}
          />

          <div className="ds-table-toolbar">
            <span>{en.common.results(flat.length)}</span>
            <SegmentedControl
              value={groupBy}
              onChange={(v) => setFilters({ groupBy: v })}
              options={[
                { value: "center", label: en.budget.byCenter },
                { value: "category", label: en.budget.byCategory },
                { value: "item", label: en.budget.byItem },
                {
                  value: "department",
                  label: en.budget.byDepartment,
                  disabled: !hasDepartment,
                  title: hasDepartment ? undefined : en.budget.departmentUnavailable,
                },
              ]}
            />
          </div>

          {groupBy === "item" ? (
            <BudgetByItemTable
              fiscalYear={filters.fiscalYear}
              fiscalMonth={filters.fiscalMonth}
              centerId={filters.centerId}
              search={filters.search}
            />
          ) : (
          <DataTable
            columns={budgetColumns()}
            rows={flat}
            isLoading={isFetching}
            density="comfortable"
            hidePagination={flat.length < 12}
            expandedKeys={expanded}
            onToggleExpand={(r) =>
              setExpanded((prev) => {
                const next = new Set(prev)
                if (next.has(r.id)) next.delete(r.id)
                else next.add(r.id)
                return next
              })
            }
            empty={
              <EmptyState
                icon={<Wallet aria-hidden />}
                title={en.budget.emptyTitle}
                text={en.budget.emptyText}
                action={
                  <Link to="/procurement/order-requests/new" style={{ fontSize: 13, color: "var(--brand-600)" }}>
                    {en.budget.createHint}
                  </Link>
                }
              />
            }
          />
          )}

          {visible.length > 0 && (
            <p className="pc-hint" style={{ marginTop: 8 }}>
              <Link to="/procurement/order-requests/new">{en.budget.createHint}</Link>
            </p>
          )}
        </>
      )}
    </Page>
  )
}

const ManagerBudgetView = () => {
  const [addOpen, setAddOpen] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())
  const [focusId, setFocusId] = useState("")
  const [treeSort, setTreeSort] = useState({ key: "util", dir: "desc" })

  const { filters, setFilters, resetFilters } = useProcurementFilters({
    fiscalYear: currentYearStr,
    fiscalMonth: "",
    centerId: "",
    search: "",
    groupBy: "center",
  })

  const { data: centers = [] } = useGetBudgetCentersQuery({ isActive: true })
  const { data: rawCategories = [] } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const categories = useMemo(
    () => rawCategories.filter((c) => !/mock/i.test(c.name || "")),
    [rawCategories]
  )

  const queryParams = useMemo(
    () => ({
      fiscalYear: filters.fiscalYear,
      fiscalMonth: filters.fiscalMonth || undefined,
      centerId: filters.centerId || undefined,
    }),
    [filters.fiscalYear, filters.fiscalMonth, filters.centerId]
  )

  const {
    data: rollup,
    isFetching: isFetchingRollup,
    isError: isRollupFetchError,
    error: rollupError,
    refetch: refetchRollup,
  } = useGetBudgetLinesQuery(queryParams)
  const rollupNotFound =
    Boolean(rollup?.notFound) || Boolean(isRollupFetchError && rollupError?.notFound)
  const rollupLines = useMemo(
    () =>
      enrichRows(rollupNotFound ? [] : rollup?.rows || [], centers).filter(
        (l) => !/mock/i.test(l.categoryName || "")
      ),
    [rollup, rollupNotFound, centers]
  )

  const isRollupError = isRollupFetchError && !rollupError?.notFound

  const [createBudgetLine, { isLoading: isCreatingLine }] = useCreateBudgetLineMutation()
  const [createBudgetCategory, { isLoading: isCreatingCategory }] = useCreateBudgetCategoryMutation()
  const isCreating = isCreatingLine || isCreatingCategory
  const activeRoleCode = useSelector((state) => getRoleCode(state.auth?.role))
  const canEditBudget = BUDGET_EDIT_ROLES.includes(activeRoleCode)
  const [modalPair, setModalPair] = useState(null)

  // Leaf rows of the center→category / category→center tree carry one source
  // budget row, which has both ids of the pair.
  const openPairModal = (node) => {
    const src = node.source || {}
    const categoryId = src.category ?? src.categoryId
    if (node.depth === 0 || src.centerId == null || categoryId == null) return
    setModalPair({
      categoryId,
      centerId: src.centerId,
      categoryName: src.categoryName,
      centerName: src.centerName,
    })
  }

  const visible = useMemo(() => {
    const q = filters.search.trim().toLowerCase()
    return rollupLines.filter((r) => !q || (r.categoryName || "").toLowerCase().includes(q) || (r.centerName || "").toLowerCase().includes(q))
  }, [rollupLines, filters.search])

  const hasDepartment = visible.some((r) => r.departmentId || r.department)
  const groupBy = filters.groupBy === "department" && !hasDepartment ? "center" : filters.groupBy
  const tree = useMemo(() => {
    const built = buildBudgetTree(visible, groupBy)
    const getVal = (n) => (treeSort.key === "util" || treeSort.key === "health" ? usedPct(n) : treeSort.key === "consumed" ? totalConsumed(n) : n[treeSort.key])
    return sortTree(built, getVal, treeSort.dir)
  }, [visible, groupBy, treeSort])
  const flat = useMemo(() => flattenTree(tree, expanded), [tree, expanded])

  const focused = focusId ? flat.filter((r) => r.id === focusId || r.id.startsWith(focusId) || focusId.startsWith(r.id)) : flat

  const kpis = useMemo(() => {
    const computed = visible.reduce(
      (acc, l) => {
        acc.total += l.total || 0
        acc.committed += l.committed || 0
        acc.unpaid += l.approvedUnpaid || 0
        acc.paid += l.approvedPaid || 0
        acc.remaining += l.remaining || 0
        acc.overPaidAmount += l.overPaidAmount || 0
        return acc
      },
      { total: 0, committed: 0, unpaid: 0, paid: 0, remaining: 0, overPaidAmount: 0 }
    )
    const s = rollupNotFound ? null : rollup?.summary
    return {
      total: s?.total ?? computed.total,
      committed: s?.committed ?? computed.committed,
      unpaid: s?.approvedUnpaid ?? computed.unpaid,
      paid: s?.approvedPaid ?? computed.paid,
      remaining: s?.remaining ?? computed.remaining,
      overPaidAmount: s?.overPaidAmount ?? computed.overPaidAmount,
      isOverBudget: s ? !!s.isOverBudget : computed.remaining < 0,
      overSpentAmount: s ? s.overSpentAmount || 0 : Math.max(0, -computed.remaining),
    }
  }, [visible, rollup, rollupNotFound])

  const used = kpis.total > 0 ? ((kpis.total - kpis.remaining) / kpis.total) * 100 : 0
  const remainAlert = remainingAlert(kpis.remaining, kpis.total)

  const attention = useMemo(
    () =>
      [...visible]
        .map((r) => ({
          ...r,
          name: `${r.centerName || ""} · ${r.categoryName || ""}`.replace(/^ · /, ""),
          pct: usedPct(r),
        }))
        .sort((a, b) => b.pct - a.pct)
        .slice(0, 5),
    [visible]
  )

  const handleCreateLine = async ({ newCategory, ...payload }, existingRow) => {
    try {
      let { categoryId } = payload
      if (newCategory) {
        const created = await createBudgetCategory(newCategory).unwrap()
        categoryId = created?.id
        if (!categoryId) throw new Error("Category was created, but we couldn't find its ID.")
      }
      const created = await createBudgetLine({ ...payload, categoryId }).unwrap()
      showToastSuccess(
        existingRow
          ? `Added ${formatVND(payload.total)} to ${String(payload.fiscalMonth).padStart(2, "0")}/${payload.fiscalYear} — new total ${formatVND(created?.totalAmount ?? existingRow.total + payload.total)}.`
          : en.toast.budgetCreated
      )
      setAddOpen(false)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this budget line. Please try again."))
    }
  }

  const centerOptions = centers.map((c) => ({ value: String(c.id), label: c.name }))

  return (
    <Page
      breadcrumb={["Procurement", "Budget control"]}
      title={en.budgetControl.title}
      subtitle={en.budgetControl.subtitle}
      actions={
        canEditBudget && (
          <Button variant="primary" leftIcon={<Plus aria-hidden />} onClick={() => setAddOpen(true)}>
            {en.budgetControl.addLine}
          </Button>
        )
      }
    >
      <KpiStrip>
        <KpiTile label={en.budgetControl.totalBudget} value={<MoneyText value={kpis.total} compact align="left" />} hint={en.common.inCurrentList} />
        <KpiTile label={en.budgetControl.committed} value={<MoneyText value={kpis.committed} compact align="left" />} hint={en.common.inCurrentList} />
        <KpiTile
          label={en.budgetControl.approvedUnpaid}
          value={<MoneyText value={kpis.unpaid} compact align="left" />}
          hint={en.budgetControl.liabilityHint}
        />
        <KpiTile
          label={en.budgetControl.approvedPaid}
          value={<MoneyText value={kpis.paid} compact align="left" />}
          hint={en.budgetControl.cashOutHint}
        />
        <KpiTile
          label={en.budgetControl.remaining}
          value={<MoneyText value={kpis.remaining} compact align="left" />}
          hint={en.budgetControl.remainingHint(Math.round(used))}
          emphasis
          danger={remainAlert.key === "critical"}
        />
        {kpis.isOverBudget && (
          <KpiTile
            label={en.budget.overSpent}
            value={<MoneyText value={kpis.overSpentAmount} compact align="left" />}
            hint={en.budget.overSpentHint}
            danger
          />
        )}
        {kpis.overPaidAmount > 0 && (
          <KpiTile
            label={en.budget.overPaid}
            value={<MoneyText value={kpis.overPaidAmount} compact align="left" />}
            hint="Paid beyond the remaining budget"
            danger
          />
        )}
      </KpiStrip>

    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 280px)", gap: 12, marginBottom: 16 }}>
        <div className="ds-attention">
          <h2>{en.budgetControl.whereMoney}</h2>
          <WaterfallBar
            committed={kpis.committed}
            unpaid={kpis.unpaid}
            paid={kpis.paid}
            remaining={kpis.remaining}
            total={kpis.total}
          />
        </div>
        <div className="ds-attention">
          <h2>{en.budgetControl.needsAttention}</h2>
          {attention.length === 0 ? (
            <p className="pc-hint">{en.budgetControl.needsAttentionEmpty}</p>
          ) : (
            attention.map((r) => (
              <button
                key={`${r.centerId}-${r.category}`}
                type="button"
                onClick={() => {
                  setFocusId(`ctr-${r.centerId}`)
                  setFilters({ search: r.categoryName || "" })
                }}
              >
                <span>
                  {r.name}
                  {/* TODO(BE): pending order count per budget line is not on GET /budgets */}
                </span>
                <StatusPill tone={budgetStatusOf(r.pct).tone} label={`${Math.round(r.pct)}%`} />
              </button>
            ))
          )}
        </div>
      </div>

      <FilterBar
        values={filters}
        onChange={setFilters}
        onReset={() => {
          setFocusId("")
          resetFilters()
        }}
        fields={[
          { key: "fiscalYear", label: en.filter.fiscalYear, type: "select", options: YEAR_OPTIONS, allowEmpty: false },
          { key: "fiscalMonth", label: en.filter.month, type: "select", options: FISCAL_MONTH_OPTIONS, allowEmpty: false },
          { key: "centerId", label: en.filter.center, type: "select", options: centerOptions, placeholder: en.filter.allCenters },
          { key: "search", label: en.filter.search, placeholder: en.filter.placeholder },
        ]}
      />

      <div className="ds-table-toolbar">
        <span>
          {en.budgetControl.budgetVsActual}
          {groupBy !== "item" && <span className="pc-hint" style={{ marginLeft: 8 }}>{en.budgetControl.editLinesHint}</span>}
        </span>
        <SegmentedControl
          value={groupBy}
          onChange={(v) => setFilters({ groupBy: v })}
          options={[
            { value: "center", label: en.budget.byCenter },
            { value: "category", label: en.budget.byCategory },
            { value: "item", label: en.budget.byItem },
            {
              value: "department",
              label: en.budget.byDepartment,
              disabled: !hasDepartment,
              title: en.budget.departmentUnavailable,
            },
          ]}
        />
      </div>

      {groupBy === "item" ? (
        <BudgetByItemTable
          fiscalYear={filters.fiscalYear}
          fiscalMonth={filters.fiscalMonth}
          centerId={filters.centerId}
          search={filters.search}
        />
      ) : isRollupError ? (
        <ErrorState
          text={getApiErrorMessage(rollupError, en.empty.loadErrorText)}
          action={
            <Button variant="secondary" size="sm" onClick={refetchRollup}>
              {en.common.retry}
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={budgetColumns({ showStatus: true })}
          rows={focused.length ? focused : flat}
          isLoading={isFetchingRollup}
          sortRows={false}
          initialSort={{ key: "util", dir: "desc" }}
          onSortChange={setTreeSort}
          onRowClick={openPairModal}
          expandedKeys={expanded}
          onToggleExpand={(r) =>
            setExpanded((prev) => {
              const next = new Set(prev)
              if (next.has(r.id)) next.delete(r.id)
              else next.add(r.id)
              return next
            })
          }
          empty={<EmptyState icon={<Wallet aria-hidden />} title={en.budget.emptyTitle} text={en.budget.emptyText} />}
        />
      )}

      {/* TODO(BE): spend trend by month needs 12 periods from GET /budgets. Current
          fiscalMonth filter returns one period or a year sum — not a monthly series. */}

      <BudgetPairModal
        pair={modalPair}
        fiscalYear={filters.fiscalYear}
        canEdit={canEditBudget}
        onClose={() => setModalPair(null)}
      />

      <AddBudgetLineModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        categories={categories}
        centers={centers}
        yearOptions={YEAR_OPTIONS}
        defaultCenterId={filters.centerId}
        defaultFiscalYear={filters.fiscalYear}
        defaultFiscalMonth={filters.fiscalMonth}
        busy={isCreating}
        onCreate={handleCreateLine}
      />
    </Page>
  )
}

const BudgetOverviewContainer = ({ oversight = false }) =>
  oversight ? <ManagerBudgetView /> : <RequesterBudgetView />

export default BudgetOverviewContainer
